/**
 * Client API REST + SSE — point d'entrée unique pour les appels backend.
 *
 * Convention : backend = camelCase, frontend = snake_case.
 * Les helpers normalize* / denormalize* font la conversion ici.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'hg_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function request(method, path, body, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    // Laisse l'appelant gérer la redirection si nécessaire
    throw new Error('Non authentifié');
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `HTTP ${res.status}`);
  }

  if (res.status === 204) return null;
  return res.json();
}

// ---------------------------------------------------------------------
// API CRUD générique
// ---------------------------------------------------------------------

export const apiDb = {
  getAll: (resource, params) => {
    const qs = params ? `?${new URLSearchParams(params)}` : '';
    return request('GET', `/${resource}${qs}`);
  },
  getById: (resource, id) => request('GET', `/${resource}/${id}`),
  create: (resource, data) => request('POST', `/${resource}`, data),
  update: (resource, id, data) => request('PUT', `/${resource}/${id}`, data),
  delete: (resource, id) => request('DELETE', `/${resource}/${id}`),
};

// ---------------------------------------------------------------------
// Helpers métier
// ---------------------------------------------------------------------

export const api = {
  health: () => request('GET', '/health'),

  // Auth
  auth: {
    googleLoginUrl: (next = '/') =>
      `${API_URL}/auth/google?next=${encodeURIComponent(next)}`,
    me: () => request('GET', '/auth/me'),
    logout: () => request('POST', '/auth/logout'),
  },

  // CRA
  cra: {
    startJob: (params) => request('POST', '/cra/jobs', params),
    preview: (params) => request('POST', '/cra/preview', params),
    retryJob: (id) => request('POST', `/cra/jobs/${id}/retry`),
    deleteJob: (id) => request('DELETE', `/cra/jobs/${id}`),
    restoreJob: (id) => request('POST', `/cra/jobs/${id}/restore`),
    listJobs: ({ limit = 20, offset = 0, includeDeleted = false } = {}) => {
      const qs = new URLSearchParams({ limit, offset });
      if (includeDeleted) qs.set('includeDeleted', 'true');
      return request('GET', `/cra/jobs?${qs}`);
    },
    getJob: (id) => request('GET', `/cra/jobs/${id}`),
    listPeople: () => request('GET', '/cra/people'),
    fileUrl: (craFileId) => {
      const token = getToken();
      return `${API_URL}/cra/files/${craFileId}/pdf?token=${encodeURIComponent(token || '')}`;
    },
  },

  // Drive
  drive: {
    getFolderName: (folderId) =>
      request('GET', `/drive/folder/${encodeURIComponent(folderId)}`),
    uploadJob: (jobId, params) =>
      request('POST', `/drive/upload/jobs/${jobId}`, params),
    uploadJobStream: (jobId, params, { onEvent, signal } = {}) =>
      streamNdjson(`/drive/upload/jobs/${jobId}`, params, { onEvent, signal }),
  },

  // Mail
  mail: {
    sendCra: (craFileId, params) =>
      request('POST', `/mail/cra/${craFileId}/send`, params),
    sendCraBatch: (craFileIds, params) =>
      request('POST', '/mail/cra/send-batch', { craFileIds, ...params }),
    listTemplates: () => request('GET', '/mail/templates'),
    createTemplate: (data) => request('POST', '/mail/templates', data),
    updateTemplate: (id, data) => request('PUT', `/mail/templates/${id}`, data),
    deleteTemplate: (id) => request('DELETE', `/mail/templates/${id}`),
  },

  // Dashboard
  dashboard: {
    stats: ({ months = 6 } = {}) => request('GET', `/dashboard/stats?months=${months}`),
  },

  // Audit (admin)
  audit: {
    list: ({ limit = 50, offset = 0, action, userId, startDate, endDate } = {}) => {
      const qs = new URLSearchParams({ limit, offset });
      if (action) qs.set('action', action);
      if (userId) qs.set('userId', userId);
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      return request('GET', `/audit?${qs}`);
    },
    exportCsvUrl: (filters = {}) => {
      const token = getToken();
      const qs = new URLSearchParams({ token: token ?? '' });
      Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
      return `${API_URL}/audit/export.csv?${qs}`;
    },
  },
};

// ---------------------------------------------------------------------
// NDJSON — stream de progression via POST + ReadableStream
// ---------------------------------------------------------------------

/**
 * Effectue un POST qui répond en `application/x-ndjson`, lit la réponse
 * ligne par ligne et invoque `onEvent` pour chaque objet JSON reçu.
 * Résout avec la dernière ligne reçue (typiquement l'événement `completed`).
 */
export async function streamNdjson(path, body, { onEvent, signal } = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/x-ndjson',
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401) {
    clearToken();
    throw new Error('Non authentifié');
  }
  if (!res.ok || !res.body) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody.error || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let last = null;

  const handleLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      const event = JSON.parse(trimmed);
      last = event;
      onEvent?.(event);
    } catch {
      // Ligne malformée : ignorée silencieusement
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      handleLine(line);
    }
  }
  if (buffer.length > 0) handleLine(buffer);
  return last;
}

// ---------------------------------------------------------------------
// SSE — stream de progression d'un job CRA
// ---------------------------------------------------------------------

/**
 * Ouvre une connexion SSE sur /cra/jobs/:id/stream. Le token est passé
 * en query string car EventSource ne supporte pas les headers customs.
 *
 * @param {string} jobId
 * @param {object} handlers
 * @param {(event: object) => void} handlers.onEvent
 * @param {(error: unknown) => void} [handlers.onError]
 * @param {() => void} [handlers.onClose]
 * @returns {() => void} fonction de cleanup
 */
export function streamCraJob(jobId, { onEvent, onError, onClose }) {
  const token = getToken();
  const url = `${API_URL}/cra/jobs/${jobId}/stream?token=${encodeURIComponent(token || '')}`;
  const source = new EventSource(url);

  const eventTypes = [
    'job_started',
    'consultant_start',
    'consultant_done',
    'project_start',
    'pdf_generated',
    'drive_uploaded',
    'mail_sent',
    'warning',
    'error',
    'job_completed',
    'job_failed',
    'stream_end',
  ];

  const listener = (e) => {
    try {
      const data = JSON.parse(e.data);
      onEvent(data);
      if (e.type === 'stream_end' || e.type === 'job_completed' || e.type === 'job_failed') {
        source.close();
        onClose?.();
      }
    } catch (err) {
      onError?.(err);
    }
  };

  for (const type of eventTypes) {
    source.addEventListener(type, listener);
  }

  source.onerror = (err) => {
    onError?.(err);
    source.close();
    onClose?.();
  };

  return () => source.close();
}

// ---------------------------------------------------------------------
// Normalisation backend ↔ frontend
// ---------------------------------------------------------------------

export function normalizeJob(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    user_id: raw.userId,
    start_date: raw.startDate,
    end_date: raw.endDate,
    status: raw.status,
    dry_run: raw.dryRun,
    started_at: raw.startedAt,
    finished_at: raw.finishedAt,
    total_consultants: raw.totalConsultants,
    total_pdfs: raw.totalPdfs,
    total_errors: raw.totalErrors,
    cra_files: raw.craFiles,
  };
}

export function normalizePerson(raw) {
  if (!raw) return null;
  return {
    people_id: raw.peopleId,
    name: raw.name,
    email: raw.email,
    active: raw.active,
  };
}
